import { performSheetUpload } from '@/api/files/api.files'
import { getAuthToken } from '@/auth/guestAuth'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
} from '@flatfile/baseline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

interface UploadModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
}

export function UploadModal({
  isOpen,
  onOpenChange,
  projectId,
}: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const queryClient = useQueryClient()

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      // Step 1: Upload the file
      const formData = new FormData()
      formData.append('file', file)
      formData.append('projectId', projectId)
      formData.append('originalName', file.name)
      formData.append('name', file.name)
      formData.append('mimeType', file.type)
      formData.append('size', file.size.toString())

      const response = await fetch(`${import.meta.env.VITE_API_URL}/files`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAuthToken()}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || 'Failed to upload file')
      }

      const uploadedFile = await response.json()

      // Step 2: Extract sheets from Excel files (CSV is handled by backend)
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        try {
          await performSheetUpload(file, uploadedFile.id, projectId)
        } catch (error) {
          console.error('Sheet extraction error:', error)
          // Don't fail the whole upload if extraction fails
        }
      }

      return uploadedFile
    },
    onSuccess: (uploadedFile) => {
      // Invalidate files and sheets queries to refetch the lists
      queryClient.invalidateQueries({ queryKey: ['files', projectId] })
      queryClient.invalidateQueries({ queryKey: ['sheets', uploadedFile.id] })
      // Close modal and reset state
      onOpenChange(false)
      setFile(null)
    },
    onError: (error) => {
      console.error('Upload error:', error)
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && isAcceptedFileType(droppedFile)) {
      setFile(droppedFile)
    }
  }

  const isAcceptedFileType = (file: File) => {
    const acceptedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ]
    const acceptedExtensions = ['.csv', '.xls', '.xlsx']

    return (
      acceptedTypes.includes(file.type) ||
      acceptedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
    )
  }

  const handleUpload = () => {
    if (file) {
      uploadMutation.mutate(file)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Upload File</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          {/* File Drop Zone */}
          <div
            className={`
              border-2 border-dashed rounded-lg p-8 text-center transition-colors
              ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}
              ${file ? 'bg-gray-50' : 'hover:border-gray-400'}
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {file ? (
              <div className='space-y-2'>
                <Icon
                  name='file-bend'
                  className='mx-auto h-12 w-12 text-gray-400'
                />
                <p className='text-sm font-medium'>{file.name}</p>
                <p className='text-xs text-gray-500'>
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <Button variant='ghost' size='sm' onClick={() => setFile(null)}>
                  Remove
                </Button>
              </div>
            ) : (
              <div className='space-y-2'>
                <Icon
                  name='upload'
                  className='mx-auto h-12 w-12 text-gray-400'
                />
                <div className='text-sm text-gray-600'>
                  <label htmlFor='file-upload' className='cursor-pointer'>
                    <span className='text-blue-600 hover:text-blue-500'>
                      Click to upload
                    </span>
                    {' or drag and drop'}
                  </label>
                  <input
                    id='file-upload'
                    name='file-upload'
                    type='file'
                    accept='.csv,.xls,.xlsx'
                    className='sr-only'
                    onChange={handleFileSelect}
                  />
                </div>
                <p className='text-xs text-gray-500'>CSV, XLS, or XLSX files</p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {uploadMutation.isError && (
            <div className='rounded-md bg-red-50 p-3'>
              <p className='text-sm text-red-800'>
                {uploadMutation.error?.message || 'Failed to upload file'}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className='flex justify-end gap-2'>
            <Button
              variant='ghost'
              onClick={() => {
                onOpenChange(false)
                setFile(null)
                uploadMutation.reset()
              }}
              disabled={uploadMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!file || uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <>
                  <Icon name='loader' className='mr-2 h-4 w-4 animate-spin' />
                  Uploading...
                </>
              ) : (
                'Upload'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
